import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useQuery } from '@/hooks/use-api';
import OptionFormFields from '@/pages/company-management/company-options/option-form-fields';
import getCompanyPriceListSelect from '@/api/get/config/get-company-price-list-select';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import { CloseIcon } from '@/components/modal/modal-style';
import { Col } from '@/components/grid/grid-component';

const FooterCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  background-color: #eaedf3 !important;
  width: 100%;
  padding: 20px;
`;

/**
 * Product option variant form
 * @param {object} props - Component props
 * @param {Function} props.closeModal - Function to close the modal
 * @param {Function} props.submitForm - Function to submit the form
 * @returns {JSX.Element} The rendered ProductOptionVariantForm component.
 */
const ProductOptionVariantForm = ({ closeModal, submitForm }) => {
  const { id: companyId } = useParams();
  const [isVariantLoading, setIsVariantLoading] = useState(true);

  const { getValues, reset, handleSubmit } = useFormContext();

  const getPricelist = useCallback(
    () => getCompanyPriceListSelect(companyId),
    [companyId],
  );

  const { data: pricelistData, loading: pricelistLoading } =
    useQuery(getPricelist);

  const variantData = getValues('option');
  const selectedPriceIds = getValues('selectedPriceIds');

  useEffect(() => {
    if (variantData && Object.keys(variantData).length > 0) {
      setIsVariantLoading(true);
      const defaultValues = {
        variant: {
          id: variantData.id || null,
          name: variantData.name || '',
          description: variantData.description || '',
          quote_description: variantData.quote_description || '',
          brochure_link: variantData.brochure_link || '',
          video_link: variantData.video_link || '',
          status: variantData.status || 'active',
          selectedPriceId:
            variantData.selectedPriceId || variantData.price_id || null,
          thumbnail: variantData.thumbnail || null,
          media: variantData.media || null,
          parent_option: variantData.parent_option || null,
          overrides: variantData.overrides || [],
        },
        selectedPriceIds,
        originalOption: variantData,
      };

      if (defaultValues) {
        reset(defaultValues);
      }
      setIsVariantLoading(false);
    }
  }, [reset, variantData, selectedPriceIds]);

  const isLoading = pricelistLoading || isVariantLoading;

  return (
    <div>
      <OptionFormFields
        fieldPrefix="variant"
        isLoading={isLoading}
        pricelistData={pricelistData}
        pricelistLoading={pricelistLoading}
        refetchPriceList={() => {}}
        originalOptions={getValues('option')}
      />
      <FooterCol xs={12}>
        <PermissionWrapper
          permissions={['option.create', 'option.update']}
          satisfyAll={false}
        >
          <PrimaryOutlineButton
            icon={<CloseIcon />}
            buttonWidth="110px"
            title="Cancel"
            type="button"
            onClick={closeModal}
          />
          <PrimaryButton
            title="Update"
            buttonWidth="110px"
            type="button"
            onClick={() => {
              handleSubmit(submitForm)();
            }}
            isLoading={isLoading}
          />
        </PermissionWrapper>
      </FooterCol>
    </div>
  );
};

export default ProductOptionVariantForm;

ProductOptionVariantForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
};
